/**
 * Carats and Emeralds.
 *
 * A carat is the smallest unit and the only thing stored, exactly as cents are
 * to a dollar. One Emerald is 100 carats. Emeralds are a DISPLAY convention
 * for large numbers — nothing anywhere stores a fractional Emerald.
 *
 * Small amounts read better as carats ("50 ✦" for a reading) and large ones as
 * Emeralds ("10 Emeralds" for a deck, "150 Emeralds" for a live video sitting).
 * Quoting a video session as "15,000 ✦" is technically right and completely
 * unreadable, which is the whole reason the bigger unit exists.
 */

/**
 * The sentinel the backend bakes into button labels — "New Reading (50 ✦)".
 *
 * It stays a plain character on the wire because a string cannot carry a
 * component. The frontend splits on it and renders <CaratMark> in its place
 * (see ChatContainerReader.renderActionLabel), so there is exactly one place
 * that decides what money looks like.
 *
 * Note ✦ is ALSO used decoratively across the app — placeholder tiles, log
 * dots, section marks, "✦ Coven" pills. Those are not currency and must not
 * become gems: 138 of the 179 glyphs in the frontend were decorative.
 */
export const CARAT_SENTINEL = '✦'

export const CARATS_PER_EMERALD = 100

/** Above this, an amount reads better in Emeralds. */
export const EMERALD_DISPLAY_THRESHOLD = 1000

export function toEmeralds(carats) {
  return (Number(carats) || 0) / CARATS_PER_EMERALD
}

export function toCarats(emeralds) {
  return Math.round((Number(emeralds) || 0) * CARATS_PER_EMERALD)
}

/**
 * Render an amount for a person.
 *
 * Pass `unit: 'carats'` or `'emeralds'` to force one; the default picks by
 * size. Emeralds drop a trailing `.00` so a round number never reads as a
 * price tag.
 */
export function formatAmount(carats, { unit = 'auto', glyph = '✦' } = {}) {
  const n = Math.round(Number(carats) || 0)
  const asEmeralds = unit === 'emeralds'
    || (unit === 'auto' && Math.abs(n) >= EMERALD_DISPLAY_THRESHOLD)

  if (!asEmeralds) return `${n.toLocaleString()} ${glyph}`

  const emeralds = n / CARATS_PER_EMERALD
  const body = Number.isInteger(emeralds)
    ? emeralds.toLocaleString()
    : emeralds.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${body} ${emeralds === 1 ? 'Emerald' : 'Emeralds'}`
}
